import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, of, ReplaySubject, Subject } from 'rxjs';
import { take, tap } from 'rxjs/operators';
import { serverLocation } from 'src/app/globals';
import { DefaultNotification } from '../../default-notification/classes';
import { TokenService } from '../../shared/services/token.service';
import { DefaultMail, IssueTransaction } from '../classes';

@Injectable({
  providedIn: 'root'
})
export class IssueTransactionService {

  private _issueTransactions: { [key: string]: ReplaySubject<IssueTransaction> } = {};
  private _allIssueTransactionsLoaded = new BehaviorSubject(false);
  private _issueTransactionsLoadedForInitialStatus: { [key: number]: ReplaySubject<IssueTransaction[]> } = {};

  private _specialTransactions = {
    1: new ReplaySubject(1),
    2: new ReplaySubject(2),
    3: new ReplaySubject(3)
  }
  private _specialTransactionsLoaded = new BehaviorSubject(false);

  constructor(
    private _httpClient: HttpClient,
    private _tokenService: TokenService
  ) {
    this.setUp();
  }

  createDefaultMail(mail: DefaultMail): Observable<Object> {
    return this._httpClient.post(`${serverLocation}/api/default-mail`, mail, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Object>;
  }

  createIssueTransaction(transaction: IssueTransaction): Observable<IssueTransaction> {
    return this._httpClient.post(`${serverLocation}/api/issue-transaction`, transaction, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<IssueTransaction>;
  }

  deleteDefaultMail(mailGuid: string): Observable<Object> {
    return this._httpClient.delete(`${serverLocation}/api/default-mail/${mailGuid}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Object>;
  }

  deleteIssueTransaction(transactionGuid: string): Observable<Object> {
    return this._httpClient.delete(`${serverLocation}/api/issue-transaction/${transactionGuid}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Object>;
  }

  getIssueTransactionById(identifier: string, soft: boolean = true): Observable<IssueTransaction> {
    if (typeof identifier !== 'string') return of(null);
    if (!soft || !this._issueTransactions[identifier]) {
      if (!this._issueTransactions[identifier]) this._issueTransactions[identifier] = new ReplaySubject(1);
      this._getIssueTransactionById(identifier).subscribe({
        next: (issueTransaction: IssueTransaction) => this._issueTransactions[identifier].next(issueTransaction),
        error: (error: HttpErrorResponse) => this._issueTransactions[identifier].error(error)
      });
    }
    return this._issueTransactions[identifier].asObservable();
  }

  getIssueTransactionAttributes(transactionGuid: string): Observable<number[]> {
    return this._httpClient.get(`${serverLocation}/api/issue-transaction/${transactionGuid}/attributes`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<number[]>;
  }

  getIssueTransactionInitialStatus(transactionGuid: string): Observable<number[]> {
    return this._getIssueTransactionInitialStatus(transactionGuid);
  }

  getTransactionDefaultMails(transactionId: string): Observable<DefaultMail[]> {
    return this._httpClient.get(`${serverLocation}/api/issue-transaction/${transactionId}/default-mails`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<DefaultMail[]>;
  }

  getTransactionDefaultNotifications(transactionId: string): Observable<DefaultNotification[]> {
    return this._httpClient.get(`${serverLocation}/api/issue-transaction/${transactionId}/default-notifications`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<DefaultNotification[]>;
  }

  getIssueTransactionByIssueStatus(issueStatus: number, soft: boolean = true): Observable<IssueTransaction[]> {
    if (typeof issueStatus !== 'number') return of(null);
    if (!soft || !this._issueTransactionsLoadedForInitialStatus[issueStatus]) {
      if (!this._issueTransactionsLoadedForInitialStatus[issueStatus]) this._issueTransactionsLoadedForInitialStatus[issueStatus] = new ReplaySubject(1);
      this
        ._getIssueTransactionsForInitialStatus(issueStatus)
        .pipe(
          tap((issueTransactions: IssueTransaction[]) => {
            for (let issueTransaction of issueTransactions) {
              if (!this._issueTransactions[issueTransaction.guid]) this._issueTransactions[issueTransaction.guid] = new ReplaySubject(1);
              this._issueTransactions[issueTransaction.guid].next(issueTransaction);
            }
          })
        ).subscribe(this._issueTransactionsLoadedForInitialStatus[issueStatus]);
    }
    return this._issueTransactionsLoadedForInitialStatus[issueStatus].asObservable();
  }

  getIssueTransactionBySpecialRole = (specialRole: number) => (this._specialTransactions[specialRole] as ReplaySubject<IssueTransaction>).asObservable();

  loadAllIssueTransactions(soft: boolean = true): Observable<IssueTransaction[]> {
    if (!soft || !this._allIssueTransactionsLoaded.value) {
      let subject = new Subject<IssueTransaction[]>();
      (this._httpClient.get(`${serverLocation}/api/issue-transaction`, {
        headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
      }) as Observable<IssueTransaction[]>).pipe(
        tap((result: IssueTransaction[]) => {
          for (let transaction of result) {
            if (!this._issueTransactions[transaction.guid]) this._issueTransactions[transaction.guid] = new ReplaySubject<IssueTransaction>(1);
            this._issueTransactions[transaction.guid].next(transaction);
          }
          this._allIssueTransactionsLoaded.next(true);
        })
      ).subscribe(subject);
      return subject.asObservable();
    }
    return combineLatest(Object.values(this._issueTransactions)).pipe(take(1));
  }

  markIssueTransactionAsExecuted(issueId: number, transactionId: string, auditNote: string): Observable<Object> {
    return this._httpClient.post(`${serverLocation}/api/issue-transaction/mark-as-executed`, {
      issueId: issueId,
      transactionId: transactionId,
      auditNote: auditNote
    }, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    });
  }

  setIssueTransactionAttributes(transactionGuid: string, attributeIdentifiers: number[]): Observable<number[]> {
    return this._httpClient.post(`${serverLocation}/api/issue-transaction/${transactionGuid}/set-attributes`, attributeIdentifiers, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<number[]>;
  }
  
  setIssueTransactionDefaultNotifications(transactionGuid: string, defaultNotificationIdentifiers: string[]): Observable<string[]> {
    return this._httpClient.post(`${serverLocation}/api/issue-transaction/${transactionGuid}/set-default-notifications`, defaultNotificationIdentifiers, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<string[]>;
  }

  setIssueTransactionInitialStatus(transactionGuid: string, statusIdentifiers: number[]): Observable<number[]> {
    return this._httpClient.post(`${serverLocation}/api/issue-transaction/${transactionGuid}/set-initial-status`, statusIdentifiers, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<number[]>;
  }

  setUp(){
    this._httpClient.get(`${serverLocation}/api/issue-transaction/special-transactions`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }).subscribe({
      next: (result) => {
        this._specialTransactions[1].next(result[1]);
        this._specialTransactions[2].next(result[2]);
        this._specialTransactions[3].next(result[3]);
        this._specialTransactionsLoaded.next(true);
      }
    });
  }

  updateDefaultMail(mail: DefaultMail): Observable<Object> {
    return this._httpClient.put(`${serverLocation}/api/default-mail`, mail, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Object>;
  }

  updateIssueTransaction(transaction: IssueTransaction): Observable<IssueTransaction> {
    return this._httpClient.put(`${serverLocation}/api/issue-transaction`, transaction, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<IssueTransaction>;
  }

  private _getIssueTransactionById(identifier: string): Observable<IssueTransaction> {
    return this._httpClient.get(`${serverLocation}/api/issue-transaction/${identifier}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<IssueTransaction>;
  }

  private _getIssueTransactionInitialStatus(identifier: string): Observable<number[]> {
    return this._httpClient.get(`${serverLocation}/api/issue-transaction/${identifier}/initial-status`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<number[]>;
  }

  private _getIssueTransactionsForInitialStatus(identifier: number): Observable<IssueTransaction[]> {
    return this._httpClient.get(`${serverLocation}/api/issue-transaction/status/${identifier}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<IssueTransaction[]>;
  }
}
